import type { ReactNode } from 'react';
import React from 'react';
import type {
  AddButton,
  AddPluginMenuConfig,
  EditorCommands,
  IToolbarItemConfigTiptap,
  MenuGroups,
  PluginAddButton,
  PluginAddButtons,
  PluginMenuItem,
  TiptapEditor,
  ToolbarButtonProps,
  ToolbarSettings,
  ToolbarType,
} from 'ricos-types';
import { PLUGIN_MENU_HORIZONTAL_MODAL_ID, PLUGIN_MENU_MODAL_ID } from 'ricos-types';
import { isDefined } from 'ts-is-present';
import { TOOLBARS } from 'wix-rich-content-editor-common';
import { AddPluginMenu } from 'wix-rich-content-toolbars-ui';
import { AddPluginMenuHorizontal } from 'wix-rich-content-toolbars-v3';

import type { PluginServices } from './editorPlugins';
import { PluginAddButtonCollisionError, RicosPluginAddButton } from './pluginAddButton';
import { getToolbarConfig } from './toolbar-utils/getToolbarConfig';
import { initToolbarSettings } from './toolbar-utils/initToolbarSettings';
import {
  getButtonsListFromConfig,
  toExternalInsertPluginToolbarItemsConfig,
} from './toolbar-utils/toToolbarItemsConfig';

export class RicosPluginAddButtons implements PluginAddButtons {
  buttons: PluginAddButton[] = [];

  services: PluginServices;

  toolbarSettings: ToolbarSettings;

  constructor(
    buttons: PluginAddButton[] = [],
    services: PluginServices,
    toolbarSettings: ToolbarSettings
  ) {
    this.buttons = buttons;
    this.services = services;
    this.toolbarSettings = toolbarSettings;
  }

  private hasDuplicate(candidate: PluginAddButton) {
    return this.buttons.find(b => b.equals(candidate));
  }

  asArray() {
    return this.buttons;
  }

  byGroup(group: MenuGroups) {
    return new RicosPluginAddButtons(
      this.buttons.filter(button => button.getGroup() === group),
      this.services,
      this.toolbarSettings
    );
  }

  byToolbar(toolbar: ToolbarType) {
    return new RicosPluginAddButtons(
      this.buttons.filter(button => button.getToolbars().includes(toolbar)),
      this.services,
      this.toolbarSettings
    );
  }

  byTag(tag) {
    const { t } = this.services;
    return this.buttons.filter(button =>
      t(button.getTags() || '')
        .toLowerCase()
        .includes(tag.toLowerCase())
    );
  }

  byIds(ids: string[]) {
    return new RicosPluginAddButtons(
      this.buttons.filter(button => ids.includes(button.getButtonId())),
      this.services,
      this.toolbarSettings
    );
  }

  register(button: AddButton) {
    const candidate = RicosPluginAddButton.of(button, this.services);
    const duplicate = this.hasDuplicate(candidate);
    if (duplicate) {
      throw new PluginAddButtonCollisionError(
        `the plugin add button ${candidate.getButton().id} conflicts with ${
          duplicate.getButton().id
        }`
      );
    }
    this.buttons.push(candidate);
  }

  unregister(button: AddButton) {
    const candidate = RicosPluginAddButton.of(button, this.services);
    this.buttons = this.buttons.filter(b => !b.equals(candidate));
  }

  toPluginMenuItems(): PluginMenuItem[] {
    return this.buttons.map(button => button.toPluginMenuItem());
  }

  toHorizontalMenuItems() {
    return this.buttons.map(b => b.toHorizontalMenuItem());
  }

  toFooterToolbarItems() {
    return this.buttons.map(b => b.toFooterToolbarItem());
  }

  registerPluginMenuModal(config?: AddPluginMenuConfig) {
    this.services.modals?.register({
      id: PLUGIN_MENU_MODAL_ID,
      Component: props => (
        <AddPluginMenu addButtons={this} addPluginMenuConfig={config} {...props} />
      ),
    });
    this.services.modals?.register({
      id: PLUGIN_MENU_HORIZONTAL_MODAL_ID,
      Component: (props: { children?: ReactNode; supportedButtonIds?: string[] }) => {
        const { supportedButtonIds } = props;
        return (
          <AddPluginMenuHorizontal
            buttons={supportedButtonIds ? this.byIds(supportedButtonIds) : this}
            {...props}
          />
        );
      },
    });
  }

  toToolbarButtonsConfig(
    toolbarType: ToolbarType,
    isMobile: boolean,
    options?: { disabled?: boolean }
  ): IToolbarItemConfigTiptap[] {
    const finalToolbarSettings = initToolbarSettings(this.toolbarSettings);
    const buttonType = isMobile ? 'mobile' : 'desktop';
    const toolbarConfig = getToolbarConfig(finalToolbarSettings, toolbarType);

    if (!toolbarConfig) {
      return this.buttons.map(b => b.toToolbarItemConfig(options));
    }

    const buttonsPerType = getButtonsListFromConfig(toolbarConfig, buttonType);

    if (!Array.isArray(buttonsPerType) || buttonsPerType.length === 0) {
      return this.buttons.map(b => b.toToolbarItemConfig(options));
    }

    return buttonsPerType
      .map(b => this.buttons.find(btn => btn.getButton().label === b))
      .filter(isDefined)
      .map(b => b.toToolbarItemConfig(options));
  }

  toExternalToolbarButtonsConfigs(
    editorCommands: EditorCommands,
    editor: TiptapEditor,
    isMobile: boolean
  ): Record<string, ToolbarButtonProps> {
    const toolbarType = TOOLBARS.INSERT_PLUGIN;
    const finalToolbarSettings = initToolbarSettings(this.toolbarSettings);
    const toolbarConfig = getToolbarConfig(finalToolbarSettings, toolbarType);
    const buttonsType = isMobile ? 'mobile' : 'desktop';

    const externalToolbarItemsConfig = toExternalInsertPluginToolbarItemsConfig(
      toolbarConfig,
      this.buttons,
      toolbarType,
      buttonsType
    );

    return externalToolbarItemsConfig.reduce((acc, b) => {
      const buttonConfig = b.toExternalToolbarButtonConfig(editorCommands, editor);
      return {
        ...acc,
        [buttonConfig.name || '']: buttonConfig,
      };
    }, {});
  }
}
